import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Message } from 'semantic-ui-react';
import { EmployeeRecordRead } from 'store/generated/portal/employee-records';
import { employeeRecApi } from 'areas/settings/employee-management/api/employeeRecordApi';

interface IndividualRecordDataModalProps {
  open: boolean;
  onClose: () => void;
  recordValueId: number;
  recordValueName: string;
  assessmentPeriodId: number;
  employeeId?: number;
  employeeName?: string;
  existingRecord?: EmployeeRecordRead | null;
}

const IndividualRecordDataModal: React.FC<IndividualRecordDataModalProps> = ({
  open,
  onClose,
  recordValueId,
  recordValueName,
  assessmentPeriodId,
  employeeId,
  employeeName,
  existingRecord,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createRecord] = employeeRecApi.useApiPortalV1EmployeeRecordsCreateMutation();
  const [updateRecord] = employeeRecApi.useApiPortalV1EmployeeRecordsUpdateMutation();

  useEffect(() => {
    if (existingRecord) {
      setValue(existingRecord.value || '');
    } else {
      setValue('');
    }
    setError(null);
  }, [existingRecord, employeeId, open]);

  const handleSubmit = async () => {
    if (!value.trim()) {
      setError('Value is required');
      return;
    }

    if (!employeeId) {
      setError('An error occurred - employee ID is missing');
      return;
    }

    try {
      if (existingRecord) {
        // Update existing record
        await updateRecord({
          id: existingRecord.id,
          employeeRecord: {
            employee: employeeId,
            value: value.trim(),
          },
        }).unwrap();
      } else {
        // Create new record
        await createRecord({
          employeeRecordCreateUpdate: {
            employee: employeeId,
            employee_record_value: recordValueId,
            assessment_period: assessmentPeriodId,
            value: value.trim(),
          },
        }).unwrap();
      }

      onClose();
    } catch (err: any) {
      setError(err?.data?.detail || 'Failed to save employee record');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        {existingRecord ? 'Edit' : 'Add'} {recordValueName}
        {employeeName && ` - ${employeeName}`}
      </Modal.Header>
      <Modal.Content>
        {error && <Message negative>{error}</Message>}
        <Form>
          <Form.Field required>
            <label>Value</label>
            <Form.Input
              placeholder={`Enter ${recordValueName}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary onClick={handleSubmit}>
          {existingRecord ? 'Update' : 'Add'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default IndividualRecordDataModal;
